import React, { useState, useEffect } from 'react';
import { X, User, MapPin, Calendar, Save, Loader2, UserPlus, MessageSquare, Clock } from 'lucide-react';
import styles from './Overtime.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../../components/Common/DatePicker/CustomDatePicker';
import toast from 'react-hot-toast';
import api from '../../../api/axios';

const OTAssignmentDrawer = ({ isOpen, onClose, onSave, record = null }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    project_site_id: '',
    date: '',
    expected_start_time: '18:00',
    expected_end_time: '22:00',
    assigned_by: '',
    remark: ''
  });

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);

  // Fetch dropdown list options dynamically from API
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [empRes, projRes] = await Promise.all([
          api.get('/employees-dropdown'),
          api.get('/projects-dropdown')
        ]);
        
        if (empRes.data && empRes.data.data) {
          setEmployees(empRes.data.data.map(emp => ({
            value: emp.id,
            label: emp.employee_name
          })));
        } else if (empRes.data) {
          setEmployees(empRes.data.map(emp => ({
            value: emp.id,
            label: emp.employee_name
          })));
        }
        
        if (projRes.data && projRes.data.data) {
          setSites(projRes.data.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        } else if (projRes.data) {
          setSites(projRes.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        }
      } catch (error) {
        console.error('Error fetching dropdown choices:', error);
        toast.error('Failed to load employee/project choices');
      }
    };

    if (isOpen) {
      fetchOptions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (record && isOpen) {
      setFormData({
        employee_id: record.employee_id || '',
        project_site_id: record.project_site_id || record.project_id || '',
        date: record.date || '',
        expected_start_time: record.expected_start_time || '18:00',
        expected_end_time: record.expected_end_time || '22:00',
        assigned_by: record.assigned_by || '',
        remark: record.remark || record.assignment_reason || record.instructions || ''
      });
    } else if (!record) {
      setFormData({
        employee_id: '',
        project_site_id: '',
        date: '',
        expected_start_time: '18:00',
        expected_end_time: '22:00',
        assigned_by: JSON.parse(localStorage.getItem('user') || '{}').name || 'Admin',
        remark: ''
      });
    }
  }, [isOpen, record]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (!formData.project_site_id) {
      toast.error('Please select a project site');
      return;
    }
    if (!formData.date) {
      toast.error('Please select an assignment date');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (record && record.id) {
        // Edit mode
        const payload = {
          punch_in_at: `${formData.date} ${formData.expected_start_time}:00`,
          punch_out_at: `${formData.date} ${formData.expected_end_time}:00`,
          remarks: formData.remark
        };
        response = await api.put(`/overtime/assignments/${record.id}/edit-timings`, payload);
      } else {
        // Create mode
        const payload = {
          employee_id: Number(formData.employee_id),
          project_id: Number(formData.project_site_id),
          ot_date: formData.date,
          expected_start_time: formData.expected_start_time,
          expected_end_time: formData.expected_end_time,
          assignment_reason: formData.remark
        };
        response = await api.post('/overtime/assign', payload);
      }

      if (response.data && response.data.success) {
        toast.success(record ? 'Assignment updated successfully' : 'OT Assigned successfully');
        if (onSave) {
          onSave(response.data.data);
        }
        onClose();
      } else {
        toast.error(response.data?.message || 'Failed to process request');
      }
    } catch (error) {
      console.error(error);
      const errMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to process request';
      if (error.response?.data?.errors) {
        const validationErrs = Object.values(error.response.data.errors).flat();
        toast.error(validationErrs[0]);
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{record ? 'Edit Assignment' : 'Assign Overtime'}</h2>
            <p>Schedule an employee for an extra shift.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            <div className={styles['form-group']}>
              <label><User size={16} /> Select Employee</label>
              <CustomSelect
                options={employees}
                value={employees.find(e => e.value === formData.employee_id)}
                onChange={(opt) => setFormData({ ...formData, employee_id: opt ? opt.value : '' })}
                placeholder="Choose Employee"
              />
            </div>

            <div className={styles['form-group']}>
              <label><MapPin size={16} /> Project Site</label>
              <CustomSelect
                options={sites}
                value={sites.find(s => s.value === formData.project_site_id)}
                onChange={(opt) => setFormData({ ...formData, project_site_id: opt ? opt.value : '' })}
                placeholder="Choose Site"
              />
            </div>

            <div className={styles['form-group']}>
              <label><Calendar size={16} /> Assignment Date</label>
              <CustomDatePicker
                selected={formData.date}
                onChange={(date) => setFormData({ ...formData, date: date ? date.toLocaleDateString('en-CA') : '' })}
              />
            </div>

            <div className={styles['form-row']}>
              <div className={styles['form-group']}>
                <label><Clock size={16} /> Start Time</label>
                <input
                  type="time"
                  className={styles['form-input']}
                  value={formData.expected_start_time}
                  onChange={(e) => setFormData({ ...formData, expected_start_time: e.target.value })}
                  required
                />
              </div>
              <div className={styles['form-group']}>
                <label><Clock size={16} /> End Time</label>
                <input
                  type="time"
                  className={styles['form-input']}
                  value={formData.expected_end_time}
                  onChange={(e) => setFormData({ ...formData, expected_end_time: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className={styles['form-group']}>
              <label><MessageSquare size={16} /> Remark / Instructions</label>
              <textarea
                className={styles['form-input']}
                style={{ minHeight: '100px', resize: 'vertical' }}
                placeholder="Add special instructions or reasons..."
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
              />
            </div>

            <div className={styles['form-group']}>
              <label><UserPlus size={16} /> Assigned By</label>
              <input
                type="text"
                value={formData.assigned_by}
                readOnly
                className={`${styles['form-input']} ${styles['readonly']}`}
              />
            </div>
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{record ? 'Update Assignment' : 'Create Assignment'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTAssignmentDrawer;

